import React, { Component } from 'react';

/**
 * Trait and rarity charts, shown inside the project modal.
 *
 * The visualisation lives in immutablesArtStats.html and is reused rather than
 * rebuilt. It reads its data from localStorage under 'statsViewerData' and
 * clears the key once consumed, so the data is written again immediately before
 * the frame is pointed at the page — that also makes re-opening the tab work.
 *
 * The single-file build inlines that page as window.__STATS_DOC, because a
 * downloaded copy has no server to request it from.
 */
class StatsPanel extends Component {
  constructor(props) {
    super(props);
    this.frame = React.createRef();
    this.state = { mounted: false };
  }

  componentDidMount() { this.load(); }

  componentDidUpdate(prev) {
    if (prev.statsData !== this.props.statsData) this.load();
  }

  load() {
    const { statsData } = this.props;
    if (!statsData || !this.frame.current) return;
    try {
      localStorage.setItem('statsViewerData', JSON.stringify(statsData));
    } catch (e) { /* private browsing; the frame will simply show its own prompt */ }

    const frame = this.frame.current;
    frame.onload = () => this.skin(frame);

    const doc = typeof window !== 'undefined' && window.__STATS_DOC;
    if (doc) frame.srcdoc = doc;
    else frame.src = 'immutablesArtStats.html?t=' + Date.now();
    this.setState({ mounted: true });
  }

  /**
   * Trim chrome the embedded copy does not need and match the surrounding page.
   *
   * The standalone file is left untouched; this only affects the copy shown
   * here. Its own heading duplicates the tab, the subtitle explains a
   * save-alongside step that does not apply, and the sidebar loads files by
   * hand — which is exactly what compiling in place replaces.
   */
  skin(frame) {
    try {
      const doc = frame.contentDocument;
      if (!doc || doc.getElementById('inapp-skin')) return;
      const style = doc.createElement('style');
      style.id = 'inapp-skin';
      style.textContent = `
        h1, #subtitle { display: none !important; }
        #sidebar, #load-area { display: none !important; }
        body { background: #fff !important; color: #14171c !important;
               font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important; }
        h2, h3, th { color: #14171c !important; }
        table, th, td, .section-tabs, .section-tab { border-color: #e2e5ea !important; }
        .section-tab.active { color: #1a73e8 !important; border-bottom-color: #1a73e8 !important; }
        a { color: #1a73e8 !important; }`;
      doc.head.appendChild(style);
      this.bridge(frame.contentWindow);
    } catch (e) { /* nothing to do if it cannot be reached */ }
  }

  /**
   * Let the charts drive the grid behind them.
   *
   * The stats page calls four functions as the user works: showDetail and
   * showDetailFromTable when a token is picked in the matrix or the table, and
   * addFilter/removeFilter when a trait value is clicked. Wrapping those is
   * enough to mirror both kinds of selection outward, and needs no change to
   * the page itself.
   *
   * Filters are reported as trait/value pairs rather than a token list, so the
   * matching editions are worked out from the metadata the app already holds.
   */
  bridge(win) {
    if (!win || win.__gridBridge) return;
    win.__gridBridge = true;
    this.filters = {};

    const wrap = (name, after) => {
      const original = win[name];
      if (typeof original !== 'function') return;
      win[name] = function () {
        const result = original.apply(this, arguments);
        try { after.apply(null, arguments); } catch (e) { /* never break the page */ }
        return result;
      };
    };

    const onPick = (tokenId) => {
      if (this.props.onSelectEdition) this.props.onSelectEdition(String(tokenId));
    };
    wrap('showDetail', onPick);
    wrap('showDetailFromTable', onPick);

    const report = () => {
      if (this.props.onFilterChange) this.props.onFilterChange({ ...this.filters });
    };
    wrap('addFilter', (trait, value) => { this.filters[trait] = value; report(); });
    wrap('removeFilter', (trait) => { delete this.filters[trait]; report(); });
  }

  render() {
    if (!this.props.statsData) return null;
    return (
      <iframe
        ref={this.frame}
        title="statistics"
        style={{ width: '100%', height: '62vh', border: '1px solid #e2e5ea', borderRadius: '6px', background: '#fff', marginTop: '0.75rem' }}
      />
    );
  }
}

export default StatsPanel;
